import React, { useCallback, useRef } from 'react';
import { Button } from 'js/components/common/Button';
import { ErrorMessage } from 'js/components/common/ErrorMessage';
import { AccessibilityElement } from 'js/components/common/AccessibilityElement';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';

import * as helpers from '../../helpers';
import * as Types from '../../types';

import style from './SelectedFiles.scss';

export const SelectedFiles: React.FC<Types.ISelectedFilesProps> = ({
    documentGroup,
    documentIndex,
    files,
    setFiles
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const fileInputLabelRef = useRef<HTMLLabelElement>(null);
    const translate = useComponentTranslations();
    const { limit = 1 } = documentGroup;

    const onInputFilesChange = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
        const timestamp = new Date().getTime();
        const target = event.target as HTMLInputElement;
        const fileList = target.files || [];
        const selectedFiles = Array.from<File>(fileList)
            .map(file => {
                (file as Types.IExtendedFile).id = file.name + timestamp;

                return file;
            }) as Types.IExtendedFile[];

        setFiles((prevFiles) => {
            if (selectedFiles.length + prevFiles.length > limit) {
                return [...prevFiles, ...selectedFiles.slice(0, limit - prevFiles.length)];
            }

            return [...prevFiles, ...selectedFiles];
        });

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [fileInputRef, limit, setFiles]);
    const uploadClick = useCallback((event: React.MouseEvent<HTMLButtonElement & HTMLLinkElement>): void => {
        if (event.target === event.currentTarget && fileInputLabelRef.current) {
            fileInputLabelRef.current.click();
        }
    }, [fileInputLabelRef]);
    const onRemoveClick = useCallback((id: string): void => {
        setFiles((prevFiles) => prevFiles.filter(file => file.id !== id));
    }, [setFiles]);

    const fileInputId = `files-upload-${documentIndex}`;

    return (
        <div className={style.container}>
            <div className={style.fileHints}>
                {limit > 1 && (
                    <span className={style.hint}>{translate('hint.limit', [limit])}</span>
                )}
                {limit === 1 && (
                    <span className={style.hint}>{translate('hint.limit.1')}</span>
                )}
                <span className={style.hint}>{translate('hint.format')}</span>
                <span className={style.hint}>{translate('hint.size')}</span>
            </div>

            {Boolean(files.length) && (
                <div>
                    {files.map(file => {
                        const fileErrorTk = helpers.getFileErrorTk(files, file);

                        return (
                            <div
                                key={file.id}
                                className={[
                                    style.selectedFileWrapper,
                                    ...fileErrorTk ? [style.invalid] : []
                                ].join(' ')}
                            >
                                <div className={style.selectedFile}>
                                    <span className={style.fileName}>{file.name}</span>
                                    <AccessibilityElement
                                        role="button"
                                        className={style.remove}
                                        aria-label={translate('removeFile.label')}
                                        onClick={() => onRemoveClick(file.id)}
                                    />
                                </div>
                                {fileErrorTk && (
                                    <ErrorMessage
                                        animate={true}
                                        classNames={{ error: style.invalidLabel }}
                                        error={translate(fileErrorTk)}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
            {files.length < limit && (
                <Button
                    size="medium"
                    view="secondary"
                    className={style.upload}
                    onClick={uploadClick}
                >
                    <label htmlFor={fileInputId} ref={fileInputLabelRef}>
                        <input
                            ref={fileInputRef}
                            type="file"
                            id={fileInputId}
                            name="files-upload"
                            className="hidden"
                            accept="image/png, image/jpeg, image/tiff, application/pdf"
                            onChange={onInputFilesChange}
                            {...limit - files.length > 1 && { multiple: true }}
                        />
                        {translate('upload')}
                    </label>
                </Button>
            )}
        </div>
    );
};
